import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService, boletosService, notasFiscaisService } from '../services/apiService';
import './DocumentosPage.css';

const DocumentosPage = () => {
  const [documentos, setDocumentos] = useState({
    boletos: [],
    notasFiscais: []
  });
  const [usuario, setUsuario] = useState({});
  const [abaSelecionada, setAbaSelecionada] = useState('boletos');
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const [filtro, setFiltro] = useState('');

  const navigate = useNavigate();

  useEffect(() => {
    // Verificar se o usuário está logado
    if (!authService.isAuthenticated()) {
      navigate('/login');
      return;
    }

    try {
      const dadosUsuarioString = localStorage.getItem('dadosUsuario');
      const dadosUsuario = JSON.parse(dadosUsuarioString || '{}');
      setUsuario(dadosUsuario);
      setDocumentos({
        boletos: [],
        notasFiscais: []
      });
      // Buscar documentos
      buscarDocumentos();
    } catch (error) {
      console.error('Erro ao carregar dados do usuário:', error);
      navigate('/login');
    }
  }, [navigate]);

  const buscarDocumentos = async () => {
    setCarregando(true);
    setErro('');

    try {
      // Requisições paralelas para melhor performance
      const [boletos, notasFiscais] = await Promise.all([
        boletosService.listarBoletos(localStorage.getItem('cnpj')),
        notasFiscaisService.listarNotasFiscais(localStorage.getItem('cnpj'))
      ]);
      setDocumentos({
        boletos,
        notasFiscais
      });
    } catch (error) {
      console.error('Erro ao buscar documentos:', error);
      setErro('Não foi possível carregar seus documentos. Tente novamente mais tarde.');
    } finally {
      setCarregando(false);
    }
  };

  const handleLogout = () => {
    authService.logout();
    navigate('/login');
  };

  const filtrarDocumentos = (documentos) => {
    if (!filtro) return documentos;

    return documentos.filter(doc =>
      doc.numero?.toLowerCase().includes(filtro.toLowerCase()) ||
      doc.data?.includes(filtro) ||
      (doc.valor && doc.valor.toString().includes(filtro))
    );
  };

  const formatarData = (dataString) => {
    if (!dataString) return '-';
    const data = new Date(dataString);
    return data.toLocaleDateString('pt-BR');
  };

  const formatarValor = (valor) => {
    if (valor === undefined || valor === null) return '-';
    return valor.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    });
  };

  const abrirDocumento = async (documento, tipo) => {
    try {
      setCarregando(true);
      if (tipo === 'boleto') {
        const urlCompleta = documento.caminho_http_doc + '/' + documento.nome_arquivo;
        console.log(urlCompleta);
        window.open(urlCompleta, '_blank');
      } else {
        const consulta = await notasFiscaisService.abrirPdfNFSe(documento.id);
        window.open(consulta.data, '_blank'); // '_blank' abre em uma nova aba          
      }
    } catch (error) {
      console.error(`Erro ao abrir ${tipo}:`, error);
      alert(`Não foi possível abrir o ${tipo}. Tente novamente mais tarde.`);
    }
    finally {
      setCarregando(false);
    }
  };

  const handleRefresh = () => {
    buscarDocumentos();
  };

  const renderTabela = () => {
    const tipoDocumento = abaSelecionada === 'boletos' ? 'boleto' : 'notaFiscal';
    const documentosFiltrados = filtrarDocumentos(documentos[abaSelecionada]);

    if (carregando) {
      return <div className="carregando">Carregando documentos...</div>;
    }

    if (erro) {
      return (
        <div className="erro-container">
          <div className="erro-mensagem">{erro}</div>
          <button className="btn-tentar-novamente" onClick={handleRefresh}>
            <i className="fas fa-sync-alt"></i> Tentar novamente
          </button>
        </div>
      );
    }

    if (documentosFiltrados.length === 0) {
      return (
        <div className="sem-resultados">
          {filtro
            ? 'Nenhum documento encontrado para o filtro aplicado.'
            : 'Nenhum documento disponível.'
          }
        </div>
      );
    }

    return (
      <div className="tabela-container">
        <table className="tabela-documentos">
          <thead>
            <tr>
              <th>Número</th>
              <th>Data</th>
              <th>Valor</th>
              <th>Status</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {documentosFiltrados.map(doc => (
              <tr key={doc.numero}>
                <td>{doc.numero || '-'}</td>
                <td>{formatarData(doc.data)}</td>
                <td>{formatarValor(doc.valor)}</td>
                <td>
                  <span className={`status ${(doc.status || '').toLowerCase()}`}>
                    {doc.status || 'Indefinido'}
                  </span>
                </td>
                <td>
                  <button
                    className="btn-acao visualizar"
                    onClick={() => abrirDocumento(doc, tipoDocumento)}
                    title="Visualizar PDF"
                  >
                    <i className="fas fa-eye"></i> Visualizar
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="documentos-container">
      <header className="documentos-header">
        <div className="header-content">
          <div className="logo">
            <h1>Portal de Documentos</h1>
          </div>

          <div className="usuario-info">
            <span>{usuario.nome || 'Usuário'}</span>
            <button className="btn-logout" onClick={handleLogout}>
              <i className="fas fa-sign-out-alt"></i> Sair
            </button>
          </div>
        </div>
      </header>

      <main className="documentos-content">
        <div className="acoes-container">
          <div className="filtro-container">
            <input
              type="text"
              className="input-filtro"
              placeholder="Filtrar por número, data ou valor..."
              value={filtro}
              onChange={(e) => setFiltro(e.target.value)}
            />
          </div>

          <button className="btn-refresh" onClick={handleRefresh} title="Atualizar">
            <i className="fas fa-sync-alt"></i>
          </button>
        </div>

        <div className="abas">
          <button
            className={`aba ${abaSelecionada === 'boletos' ? 'ativa' : ''}`}
            onClick={() => setAbaSelecionada('boletos')}
          >
            <i className="fas fa-file-invoice-dollar"></i> Boletos
          </button>
          <button
            className={`aba ${abaSelecionada === 'notasFiscais' ? 'ativa' : ''}`}
            onClick={() => setAbaSelecionada('notasFiscais')}
          >
            <i className="fas fa-file-alt"></i> Notas Fiscais
          </button>
        </div>

        {renderTabela()}
      </main>

      <footer className="documentos-footer">
        <p>&copy; {new Date().getFullYear()} - Portal de Documentos | Todos os direitos reservados</p>
      </footer>
    </div>
  );
};

export default DocumentosPage;